import _objectWithoutProperties from 'babel-runtime/helpers/objectWithoutProperties';
import _Object$getPrototypeOf from 'babel-runtime/core-js/object/get-prototype-of';
import _classCallCheck from 'babel-runtime/helpers/classCallCheck';
import _createClass from 'babel-runtime/helpers/createClass';
import _possibleConstructorReturn from 'babel-runtime/helpers/possibleConstructorReturn';
import _inherits from 'babel-runtime/helpers/inherits';
import _defineProperty from 'babel-runtime/helpers/defineProperty';
import _extends from 'babel-runtime/helpers/extends';
import { Component, cloneElement, h } from 'preact';
import { PureComponent } from 'shared-components';
import { Link } from 'react-router-dom';

/*
  usage:

    import enhanceWithGA, { modifyCart, checkout, pageView } from './middlewares/ga';
    import { LOCATION_CHANGE }                              from "react-router-redux";
    import { ADDING  as CART_ADDING }                       from './modules/cart';
    import {  SUCCESS as CHECKOUT_SUCCESS }                 from './modules/checkout';

    const ga = enhanceWithGA({
      accounts:     config.googleAnalytics.accounts,
      currencyCode: config.googleAnalytics.locale,
      events:       {
        [LOCATION_CHANGE]:  pageView,
        [CART_ADDING]:      modifyCart,
        [CHECKOUT_SUCCESS]: checkout,
      }
    });

    const store = createStore(reducer, state, compose(applyMiddleware(...middleware), ga));

  Included events:

   - pageView
   - modifyCart
   - checkout

  All events have the signature `(ga: GoogleAnalytics function, action: ReduxAction) => void`.

*/

var GA_CONTEXT_KEY = "@@googleAnalytics";
var GA_IMPRESSION_KEYS = ["id", "name", "list", "brand", "category", "price", "position"];
var ELEMENT_NAME = "script";
var SCRIPT_URL = "https://www.google-analytics.com/analytics.js";
var ANALYTICS_PROPERTY = "ga";

var impressionIdCounter = 0;

/**
 * Generates a new unique id (unique over the runtime of the app).
 */
var getNextImpressionId = function getNextImpressionId() {
  return impressionIdCounter++;
};

/**
 * Queues up an action to run on next render.
 */
var next = typeof window !== "undefined" && window.requestAnimationFrame ? function (cb, _timeout) {
  return window.requestAnimationFrame(cb);
} : function (cb, timeout) {
  return setTimeout(cb, timeout);
};

/**
 * Copies the listed keys to a new object.
 */
var onlyKeys = function onlyKeys(object, keys) {
  return keys.reduce(function (a, k) {
    if (object.hasOwnProperty(k)) {
      a[k] = object[k];
    }

    return a;
  }, {});
};

/**
 * Converts a tracker to a suitable GoogleAnalytics identifier.
 */
var trackerToName = function trackerToName(t) {
  return t.replace(/-/g, '');
};

/**
 * Returns an absolute path without hostname from a location descriptor.
 *
 * NOTE: We have our own here since we do not want the hostname, and the
 * history-implementation is different in server and client. We do not want
 * to rely on a specific browser/server-API being available.
 */
var createPath = function createPath(location) {
  return location.pathname + location.search + (location.hash || "");
};

/**
 * This uses the global window.ga to queue up google analytics actions.
 */
var callAccounts = function callAccounts(accounts) {
  return function () {
    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (!process.browser) {
      throw new Error("Attempting to call ga.callAccounts on server.");
    }

    // Skip if GA is not initialized yet
    if (!window[ANALYTICS_PROPERTY]) {
      return;
    }

    // Create commands cannot be prefixed
    if (args[0] === "create") {
      return window[ANALYTICS_PROPERTY].apply(null, args);
    }

    return accounts.forEach(function (account) {
      return window[ANALYTICS_PROPERTY].apply(null, args.map(function (a, i) {
        return i === 0 ? trackerToName(account) + "." + a : a;
      }));
    });
  };
};

/**
 * Initializes the Google Analytics accounts and provides a shim for recording until
 * init() has been called and its script has loaded.
 *
 * NOTE: This will enable recording events, but they will not be uploaded if
 * start() is never called.
 */
var init = function init(accounts, currencyCode) {
  window['GoogleAnalyticsObject'] = ANALYTICS_PROPERTY;
  window[ANALYTICS_PROPERTY] = window[ANALYTICS_PROPERTY] || function () {
    for (var _len2 = arguments.length, args = Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }

    return (window[ANALYTICS_PROPERTY].q = window[ANALYTICS_PROPERTY].q || []).push(args);
  };
  window[ANALYTICS_PROPERTY].l = 1 * new Date();

  accounts.map(function (account) {
    return ['create', account, 'auto', { name: trackerToName(account) }];
  }).concat([['set', 'anonymizeIp', true], ['require', 'ec'], ['require', 'ecommerce'], ['set', 'currencyCode', currencyCode]]).forEach(function (args) {
    return callAccounts(accounts).apply(null, args);
  });
};

/**
 * Starts the Google Analytics script which will collect and upload data.
 *
 * NOTE: No logs will be uploaded before the script loaded by this function
 * has been loaded.
 */
var start = function start() {
  var a = document.createElement(ELEMENT_NAME);
  var m = document.getElementsByTagName(ELEMENT_NAME)[0];

  a.async = 1;
  a.src = SCRIPT_URL;

  m.parentNode.insertBefore(a, m);
};

/**
 * Registers a pageview with action.path as the history location object.
 */
var pageView = function pageView(g, action) {
  return g("send", "pageview", createPath(action.payload));
};

/**
 * Registers an ecommerce view event for a product detail page.
 */
var productView = function productView(g, action) {
  var product = action.payload.product;

  g('ec:addProduct', {
    id: product.sku,
    name: product.name,
    brand: product.attributes.manufacturer
  });
  g('ec:setAction', 'detail');
  g('send', 'event', 'ecommerce', 'view', product.name);
};

var modifyCart = function modifyCart(g, action, type) {
  var _action$payload = action.payload,
      list = _action$payload.list,
      position = _action$payload.position,
      product = _action$payload.product,
      qty = _action$payload.qty;


  g('ec:addProduct', {
    id: product.sku,
    name: product.name,
    brand: product.attributes.manufacturer,
    price: product.price,
    list: list,
    position: position,
    quantity: qty
  });
  g('ec:setAction', type);
  g('send', 'event', 'ecommerce', 'click', type = 'add to cart');
};

var checkout = function checkout(g, action) {
  if (!action.payload) {
    return;
  }

  var quote = action.payload.quote || action.payload.cart;

  if (!quote) {
    return;
  }

  var items = quote.items;

  for (var i = 0; i < items.length; i++) {
    g('ec:addProduct', {
      id: items[i].product.sku,
      name: items[i].product.name,
      brand: items[i].product.attributes.manufacturer,
      price: (items[i].rowTotal - items[i].rowTax) / items[i].qty,
      quantity: items[i].qty
    });
  }

  g('ec:setAction', 'checkout', {
    step: 1
  });

  g('send', 'event', 'ecommerce', 'loaded', 'checkout');
};

var purchaseSuccess = function purchaseSuccess(g, action) {
  if (!action.payload) {
    return;
  }

  var order = action.payload;
  var quote = order.quote || order.cart;

  if (!quote) {
    return;
  }

  var items = quote.items;

  for (var i = 0; i < items.length; i++) {
    g('ec:addProduct', {
      id: items[i].product.sku,
      name: items[i].product.name,
      brand: items[i].product.attributes.manufacturer,
      price: (items[i].rowTotal - items[i].rowTax) / items[i].qty,
      quantity: items[i].qty
    });
  }

  g('ec:setAction', 'purchase', {
    id: order.id,
    revenue: order.quote.summary.grandTotalExclTax,
    tax: order.quote.summary.tax,
    shipping: order.quote.summary.shippingAmountExclTax,
    coupon: order.quote.summary.coupon ? order.quote.summary.coupon.ruleName : null
  });

  g('send', 'event', 'ecommerce', 'success', 'checkout');
};

/**
 * Parameters:
 *
 * * accounts
 *
 *   List of Google Analytics identifiers
 *
 * * currencyCode
 *
 *   Google Analytics currency code, sent as currencyCode
 *
 * * events
 *
 *   Map of redux actions and handlers to listen to
 */
var enhanceWithGA = function enhanceWithGA(_ref) {
  var accounts = _ref.accounts,
      currencyCode = _ref.currencyCode,
      events = _ref.events,
      _ref$analyticsStoreKe = _ref.analyticsStoreKey,
      analyticsStoreKey = _ref$analyticsStoreKe === undefined ? "googleAnalytics" : _ref$analyticsStoreKe;

  init(accounts, currencyCode);

  if (process.env.NODE_ENV !== "production") {
    console.log('GA Enhancer initialized');
  }

  return function (createStore) {
    return function (reducer, preloadedState, enhancer) {
      var _extends2;

      var store = createStore(reducer, preloadedState, enhancer);
      var g = callAccounts(accounts);

      var observeAction = function observeAction(action) {
        if (!events[action.type]) {
          return;
        }

        var func = events[action.type];

        if (process.env.NODE_ENV !== "production") {
          console.log("GA Enhancer: on '" + action.type + "' triggered callback " + func.name);
        }

        func(g, action);
      };

      var dispatch = function dispatch(action) {
        observeAction(action);

        return store.dispatch(action);
      };

      return _extends({}, store, (_extends2 = {}, _defineProperty(_extends2, analyticsStoreKey, g), _defineProperty(_extends2, "dispatch", dispatch), _extends2));
    };
  };
};

/**
 * Wraps a single child (defaults to an empty <span />) and registers a `ref` callback on it.
 */
var ProductImpression = function (_PureComponent) {
  _inherits(ProductImpression, _PureComponent);

  function ProductImpression(props) {
    _classCallCheck(this, ProductImpression);

    var _this = _possibleConstructorReturn(this, (ProductImpression.__proto__ || _Object$getPrototypeOf(ProductImpression)).call(this, props));

    _this.registerRef = function (el) {
      if (!_this.context[GA_CONTEXT_KEY]) {
        if (process.env.NODE_ENV !== "production") {
          console.error("Missing wrapping <WithGoogleAnalytics /> for <ProductImpression />");
        }

        return;
      }

      if (el) {
        _this.el = el;
      } else if (_this.id) {
        _this.context[GA_CONTEXT_KEY].unregister(_this.id);

        _this.id = null;
      }
    };

    return _this;
  }

  _createClass(ProductImpression, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      var _this2 = this;

      if (this.el) {
        setTimeout(function () {
          return _this2.id = _this2.context[GA_CONTEXT_KEY].register(_this2.el, _this2.props);
        }, this.props.delay);
      }
    }
  }, {
    key: "render",
    value: function render(_ref2) {
      var _ref2$children = _ref2.children,
          children = _ref2$children === undefined ? [] : _ref2$children;

      return cloneElement(children[0] || h("span", null), { ref: this.registerRef });
    }
  }]);

  return ProductImpression;
}(PureComponent);

/**
 * <Link /> replacement notifying goolgle analytics about the clicked product, accepts all of <Link />'s props
 * in addition to the ones listed on this class.
 */
ProductImpression.defaultProps = {
  component: h("span", null),
  delay: 200
};
var ProductLink = function (_Link) {
  _inherits(ProductLink, _Link);

  function ProductLink(props) {
    _classCallCheck(this, ProductLink);

    var _this3 = _possibleConstructorReturn(this, (ProductLink.__proto__ || _Object$getPrototypeOf(ProductLink)).call(this, props));

    var parentHandleClick = _this3.handleClick;

    // We need to bind it like this to be able to call original
    _this3.handleClick = function (event) {
      if (_this3.context[GA_CONTEXT_KEY]) {
        var _this3$props = _this3.props,
            _productId = _this3$props.productId,
            _productName = _this3$props.productName,
            _brand = _this3$props.brand,
            _category = _this3$props.category,
            _variant = _this3$props.variant,
            _position = _this3$props.position,
            _list = _this3$props.list;


        _this3.context[GA_CONTEXT_KEY].registerProductClick({
          id: _productId,
          name: _productName,
          brand: _brand,
          category: _category,
          variant: _variant,
          position: _position
        }, _list);
      } else if (process.env.NODE_ENV !== "production" && typeof window !== "undefined") {
        console.error("<ProductLink /> must be wrapped in a <WithGoogleAnalytics />");
      }

      parentHandleClick(event);
    };
    return _this3;
  }

  _createClass(ProductLink, [{
    key: "render",
    value: function render() {
      var tmp = this.props;
      // Exclude <ProductLink /> specific attributes

      var _props = this.props,
          productId = _props.productId,
          productName = _props.productName,
          list = _props.list,
          brand = _props.brand,
          category = _props.category,
          variant = _props.variant,
          position = _props.position,
          props = _objectWithoutProperties(_props, ["productId", "productName", "list", "brand", "category", "variant", "position"]);

      this.props = props;

      var dom = Link.prototype.render.call(this);

      this.props = tmp;

      return dom;
    }
  }]);

  return ProductLink;
}(Link);

/**
 * Class adding a google analytics callback to the context, use with <ProductImpression> and
 * <PageView>.
 *
 * Renders a single wrapped child.
 */
var WithGoogleAnalytics = function (_Component) {
  _inherits(WithGoogleAnalytics, _Component);

  function WithGoogleAnalytics(props) {
    _classCallCheck(this, WithGoogleAnalytics);

    var _this4 = _possibleConstructorReturn(this, (WithGoogleAnalytics.__proto__ || _Object$getPrototypeOf(WithGoogleAnalytics)).call(this, props));

    _this4.onScroll = function () {
      if (!_this4.throttle) {
        _this4.throttle = next(_this4.calculateVisible, _this4.props.timeout);
      }
    };

    _this4.calculateVisible = function () {
      _this4.throttle = null;

      var margin = _this4.props.margin;
      var windowHeight = window.innerHeight || document.documentElement.clientHeight;
      var windowWidth = window.innerWidth || document.documentElement.clientWidth;
      var visible = [];
      var rect = void 0;

      for (var i = 0; i < _this4.elements.length; i++) {
        if (!_this4.elements[i]) {
          continue;
        }

        rect = _this4.elements[i].el.getBoundingClientRect();

        if (rect.top <= windowHeight + margin && rect.top + rect.height >= margin && rect.left <= windowWidth + margin && rect.left + rect.width >= margin) {
          visible.push(i);
        }
      }

      for (var i = 0; i < visible.length; i++) {
        _this4.props.ga("ec:addImpression", onlyKeys(_this4.elements[visible[i]].data, GA_IMPRESSION_KEYS));

        delete _this4.elements[visible[i]];
      }
    };

    _this4.elements = [];
    return _this4;
  }

  _createClass(WithGoogleAnalytics, [{
    key: "register",
    value: function register(el, data) {
      var id = getNextImpressionId();

      this.elements[id] = {
        el: el,
        data: data
      };

      this.onScroll();

      return id;
    }
    /**
     * Unregisters the impression-element associated with the id obtained from `register`.
     */

  }, {
    key: "unregister",
    value: function unregister(id) {
      delete this.elements[id];
    }
  }, {
    key: "registerProductClick",
    value: function registerProductClick(product, list) {
      this.props.ga("ec:addProduct", product);

      this.props.ga("ec:setAction", "click", {
        "list": list
      });
    }
  }, {
    key: "registerCheckoutSuccess",
    value: function registerCheckoutSuccess(products, data) {
      var ga = this.props.ga;

      products.forEach(function (p) {
        return ga("ec:addProduct", p);
      });

      ga('ec:setAction', 'purchase', data);

      ga('send', 'event', 'ecommerce', 'success', 'checkout');
    }
  }, {
    key: "getChildContext",
    value: function getChildContext() {
      return _defineProperty({}, GA_CONTEXT_KEY, this);
    }
  }, {
    key: "componentDidMount",
    value: function componentDidMount() {
      this.onScroll();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      window.removeEventListener('scroll', this.onScroll, { passive: true });
      window.removeEventListener('resize', this.onScroll, { passive: true });
    }
  }, {
    key: "render",
    value: function render(_ref4) {
      var _ref4$children = _ref4.children,
          children = _ref4$children === undefined ? [] : _ref4$children;

      if (process.env.NODE_ENV !== "production" && children.length !== 1) {
        console.error("<WithGoogleAnalytics /> expects a single child");
      }

      return children[0];
    }
  }]);

  return WithGoogleAnalytics;
}(Component);

WithGoogleAnalytics.childContextTypes = _defineProperty({}, GA_CONTEXT_KEY, true);
WithGoogleAnalytics.defaultProps = {
  timeout: 50,
  margin: -50
};

export { GA_CONTEXT_KEY, start, pageView, productView, modifyCart, checkout, purchaseSuccess, ProductImpression, ProductLink, WithGoogleAnalytics };
export default enhanceWithGA;
